// Shared money math for post-order totals blocks (order detail, public
// receipt, live orders, printable ticket). Three fee sources exist on an
// order: the platform fee (order.fee), CRV (order.crvtax) and the store's own
// extra fees (order.extrafees / order.totalextrafee, e.g. "Card Processing
// Fee"). The first two fold into the tax line and its ⓘ breakdown; store
// extra fees are itemised as their own rows directly under it, so the visible
// rows still reconcile with order.total without the tax line hiding them.

const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// Whether the "Sales Tax" row should become "Sales Tax & Fees" with the ⓘ
// breakdown. Only the platform fee and CRV live in that breakdown, so only
// they open it — store extra fees never do.
export const hasTaxFeesBreakdown = (order) =>
  Boolean(order && (n(order.fee) || n(order.crvtax)));

// The amount on that row. Never gated: when there are no fees every addend is
// 0 and the result is exactly the tax.
export const taxAndFeesTotal = (order) =>
  n(order?.tax) + n(order?.fee) + n(order?.crvtax);

// One totals row per store extra fee, rendered under the tax line. $0 rows
// are dropped; negative rows (credits) stay so the rows keep summing to the
// total. Older/partial payloads (Order/ChangeStatus) carry totalextrafee
// without the extrafees array — those collapse to a single "Fees" row.
export const extraFeeRows = (order) => {
  const rows = (order?.extrafees || [])
    .map((fee) => ({
      name: String(fee?.name ?? '').trim() || 'Fee',
      price: n(fee?.price),
    }))
    .filter((row) => row.price !== 0);
  if (rows.length) return rows;
  const total = n(order?.totalextrafee);
  return total > 0 ? [{ name: 'Fees', price: total }] : [];
};

// What one order line charged, for the live orders panel (spec 2026-10-25
// A1, owner decision P). `total` is the stored row: the unit price times the
// units the customer paid for, plus every option. A coupon's free unit
// (Spend Get Free Item at qty >= 2, Buy X Get Y) keeps the unit `price` on
// the row and stays out of `total`, so quantity x price overstates the line.
// The item fee is charged on paid units only, as the checkout preview
// charges it (BasketService: itemfees += itemfee * paidQuantity). Paid units
// come back from the row as (total - optionprice) / price, rounded because
// cents do not divide exactly in floating point (20.97 / 6.99 is
// 2.9999999999999996); a row with price 0 (the qty-1 free line) has none.
export const lineAmount = (item, itemfee) => {
  const price = n(item?.price);
  const paidUnits =
    price > 0
      ? Math.round((n(item?.total) - n(item?.optionprice)) / price)
      : 0;
  return n(item?.total) + n(itemfee) * paidUnits;
};

// Mirrors the api's payout formula (DashboardController.GetReport /
// GetNetPayout, PayoutPostingService): CRV and store extra fees stay with the
// restaurant, the higher of stripefee/cardfee is the processing cost.
export const netPayout = (order) =>
  n(order?.subtotal) +
  n(order?.tax) +
  n(order?.tip) +
  n(order?.crvtax) +
  n(order?.totalextrafee) -
  Math.max(n(order?.stripefee), n(order?.cardfee)) -
  n(order?.discount) -
  n(order?.thiswayfee) -
  n(order?.refund);
